// Full editorial content for the blog. Each body paragraph is a string or a "quote" object.

export const BLOGS = [
  {
    slug: "types-of-millets",
    title: "The Nine Millets You Should Actually Know",
    dek: "A field-guide to the grain family that's been feeding humans longer than wheat, rice, or corn.",
    tag: "Basics",
    accent: "#f2cd1a",
    readTime: "6 min read",
    image:
      "https://images.pexels.com/photos/16977456/pexels-photo-16977456.jpeg?auto=compress&cs=tinysrgb&w=940",
    body: [
      "Millet is not one grain. It's a whole family — small, hardy seeds that dozens of cultures have quietly depended on for 10,000 years. The West forgot them when wheat, rice, and corn industrialised. Here are the nine that matter.",
      { type: "h2", text: "The majors" },
      { type: "h3", text: "1. Pearl Millet (Bajra)" },
      "The workhorse. High in iron, magnesium, and protein. Thrives in 40°C heat and almost no rainfall. This is the one Indian grandmothers press into roti.",
      { type: "h3", text: "2. Finger Millet (Ragi)" },
      "The calcium king — more than double what's in cow's milk, gram for gram. Slightly earthy, used in porridge, idli, and ancient South Indian babies' first solid food.",
      { type: "h3", text: "3. Foxtail Millet" },
      "Mild, rice-like, and fast-cooking. The most globally-grown millet outside of pearl. A perfect rice swap if you want the glycemic index cut in half.",
      { type: "h3", text: "4. Proso Millet" },
      "Short-season, drought-tolerant, and quietly high in lecithin (great for brain health). Popular in Eastern Europe and now reappearing in North American health stores.",
      { type: "h2", text: "The minors — but mighty" },
      { type: "h3", text: "5. Barnyard Millet" },
      "The fastest growing of the family — ready in 45 days. Low-calorie, high-fiber, and shows up in fasting diets across India.",
      { type: "h3", text: "6. Kodo Millet" },
      "An ancient, slightly nutty grain. Rich in antioxidants and being studied for its role in diabetes management.",
      { type: "h3", text: "7. Little Millet" },
      "Literally smaller than the others. Closest in texture to couscous. Perfect for salads and grain bowls.",
      { type: "h3", text: "8. Browntop Millet" },
      "The rarest of the Indian millets. Almost extinct twenty years ago — now being revived by smallholder farmers in the Deccan.",
      { type: "h3", text: "9. Sorghum (Jowar)" },
      "Technically its own genus but travels in the millet family. The gluten-free flour powering the new wave of global baking.",
      {
        type: "quote",
        text: "Nine grains. One family. Zero need for a PR team.",
      },
      "If you only try one this week: start with foxtail as rice, or ragi in your smoothie. You'll feel the difference by day three.",
    ],
  },
  {
    slug: "benefits-of-millets",
    title: "What Millet Actually Does Inside Your Body",
    dek: "Not a superfood claim. A nutrient breakdown, minus the marketing.",
    tag: "Health",
    accent: "#FF4200",
    readTime: "5 min read",
    image:
      "https://images.pexels.com/photos/16977457/pexels-photo-16977457.jpeg?auto=compress&cs=tinysrgb&w=940",
    body: [
      "We'll skip the adjectives. Here's what happens when you replace refined wheat or white rice with a bowl of millet.",
      { type: "h2", text: "1. Slower glucose curves" },
      "Most millets land at 50-60 on the glycemic index. White rice sits at 73. That's the difference between a steady afternoon and the 3pm crash.",
      { type: "h2", text: "2. More iron, more magnesium" },
      "Pearl millet delivers ~8mg of iron per 100g — double wheat's. Magnesium supports 300+ enzymatic reactions, including how your muscles recover.",
      { type: "h2", text: "3. A gut the bacteria like" },
      "Millets are rich in resistant starch and insoluble fiber — the kind your microbiome ferments into short-chain fatty acids. That's the stuff that keeps your gut lining intact.",
      {
        type: "quote",
        text: "It's not magic. It's just food that wasn't engineered to be cheap.",
      },
      { type: "h2", text: "4. Naturally gluten-free" },
      "No cross-contamination panic. No celiac risk. No bloating debate.",
      { type: "h2", text: "5. Plant protein with a better profile" },
      "Finger millet and pearl millet hit 11-13g protein per 100g — competitive with oats, with a more complete amino acid spread.",
      "Eat it three times a week for a month. Track your energy. You'll see it.",
    ],
  },
  {
    slug: "where-are-millets-found",
    title: "Where Millets Grow — And Why That Matters",
    dek: "From the Deccan Plateau to Saskatchewan, here's who's actually planting this grain.",
    tag: "Origin",
    accent: "#41c1f6",
    readTime: "4 min read",
    image:
      "https://images.pexels.com/photos/33786726/pexels-photo-33786726.jpeg?auto=compress&cs=tinysrgb&w=940",
    body: [
      "Millet is one of the few major grains that didn't get domesticated in the Fertile Crescent. It came from Africa and Asia — and that's shaped everything about it.",
      { type: "h2", text: "India — the beating heart" },
      "India produces roughly 40% of the world's millet. Rajasthan, Karnataka, Andhra Pradesh, and Tamil Nadu carry the bulk. It's the default grain of the Deccan plateau.",
      { type: "h2", text: "West Africa — the original home" },
      "Pearl millet was domesticated in the Sahel ~4,500 years ago. Niger, Nigeria, and Mali still grow millet as a staple, not a trend.",
      { type: "h2", text: "China — the ancient thread" },
      "Foxtail and proso millet fed the early Yellow River civilisations. China still grows more proso than anyone else.",
      { type: "h2", text: "North America — the new frontier" },
      "Canadian prairies and the US Dakotas are quietly scaling proso millet as a rotation crop. Less water than wheat, better for the soil.",
      {
        type: "quote",
        text: "Millet grew where wheat refused to.",
      },
      "That resilience is why the UN named 2023 the International Year of Millets. And why our supply comes from rain-fed, smallholder farms — not giant mono-cultures.",
    ],
  },
  {
    slug: "top-5-millet-recipes",
    title: "Top 5 Millet Recipes You'll Actually Make Twice",
    dek: "No 27-ingredient nonsense. Five recipes, under 40 minutes each.",
    tag: "Kitchen",
    accent: "#ff4200",
    readTime: "7 min read",
    image:
      "https://images.pexels.com/photos/1640775/pexels-photo-1640775.jpeg?auto=compress&cs=tinysrgb&w=940",
    body: [
      "Forget the recipe blogs that make you scroll past a life story. Here's the whole list.",
      { type: "h2", text: "1. Foxtail Millet Khichdi (25 min)" },
      "1 cup foxtail + 1/2 cup moong dal, rinsed. Pressure cook with 4 cups water, 1 tsp turmeric, 1 tsp salt, 3 whistles. Temper with ghee, cumin, hing, curry leaves. Done.",
      { type: "h2", text: "2. Ragi Banana Pancakes (15 min)" },
      "1/2 cup ragi flour, 1 ripe banana, 1 egg (or 1 flax egg), 1/2 cup Milet Milk, pinch of cinnamon. Blend, pan-fry. Earthy, slightly sweet, bulletproof breakfast.",
      { type: "h2", text: "3. Barnyard Millet Salad Bowl (20 min)" },
      "Cook 1 cup barnyard millet like rice (2:1 water). Cool. Toss with cucumber, cherry tomato, feta, olives, lemon, olive oil. Couscous energy, millet nutrition.",
      { type: "h2", text: "4. Pearl Millet Bajra Roti (30 min)" },
      "2 cups bajra flour + warm water + salt. Knead into a soft dough, pat between palms, cook on a hot skillet, finish over open flame. The grain of Rajasthan.",
      { type: "h2", text: "5. Milet Milk Cold Brew Latte (5 min)" },
      "Pour cold brew concentrate over ice. Top with Milet Milk. No frother needed — it naturally froths. This replaced oat milk in our kitchen months ago.",
      {
        type: "quote",
        text: "Good food shouldn't need 15 tabs open.",
      },
      "Start with one. Come back for the other four when the first one becomes a habit.",
    ],
  },
  {
    slug: "millet-vs-wheat",
    title: "Millet vs Wheat — The Fight No One Started",
    dek: "Why the grain you eat every day was a compromise, and what a better one looks like.",
    tag: "Showdown",
    accent: "#f2cd1a",
    readTime: "5 min read",
    image:
      "https://images.pexels.com/photos/33786724/pexels-photo-33786724.jpeg?auto=compress&cs=tinysrgb&w=940",
    body: [
      "Wheat won the 20th century. It's cheap, it's scalable, it feeds billions. None of that makes it the grain your body prefers.",
      { type: "h2", text: "Gluten: the elephant" },
      "Wheat is ~10-13% gluten by weight. For celiacs it's poison. For a significant chunk of everyone else it's quiet inflammation. Millet has zero.",
      { type: "h2", text: "Glycemic response" },
      "Refined wheat flour sits at 71 on the GI scale. Pearl millet roti lands around 55. That's the difference between stable mornings and sugar-crash afternoons.",
      { type: "h2", text: "Water footprint" },
      "1 kg of wheat ≈ 1,800 L of water. 1 kg of pearl millet ≈ 650 L. The math keeps getting worse for wheat as the planet warms.",
      {
        type: "quote",
        text: "Wheat was optimised for yield. Millet was optimised for survival.",
      },
      "We're not anti-wheat. We're pro-options. And the option you've been missing is millet.",
    ],
  },
  {
    slug: "millet-vs-oats",
    title: "Millet vs Oats — A Closer Race Than You Think",
    dek: "Oats get all the morning glory. Here's where millet quietly wins.",
    tag: "Showdown",
    accent: "#FF4200",
    readTime: "4 min read",
    image:
      "https://images.pexels.com/photos/1775043/pexels-photo-1775043.jpeg?auto=compress&cs=tinysrgb&w=940",
    body: [
      "Oats are genuinely great. This isn't a hit piece. It's a comparison — and millet deserves to be in the conversation.",
      { type: "h2", text: "Protein" },
      "Millet edges oats: ~11g vs 10.6g per 100g. Margins, but millet's amino acid spread is more complete.",
      { type: "h2", text: "Gluten contamination" },
      "Oats are naturally gluten-free but almost always contaminated at harvest or processing. Millet is gluten-free end-to-end.",
      { type: "h2", text: "Water" },
      "Millet: ~650 L/kg. Oats: ~1,800 L/kg. A meaningful gap in a warming world.",
      {
        type: "quote",
        text: "Oats get a head start. Millet finishes the race.",
      },
      "Both can live in your kitchen. But if you're only picking one, pick the one that's quieter about its nutrition.",
    ],
  },
  {
    slug: "millet-vs-almond",
    title: "Millet vs Almond — The Water Problem",
    dek: "Your almond milk is mostly water. Ours is mostly millet.",
    tag: "Showdown",
    accent: "#ff4200",
    readTime: "4 min read",
    image:
      "https://images.pexels.com/photos/1013420/pexels-photo-1013420.jpeg?auto=compress&cs=tinysrgb&w=940",
    body: [
      "Almond milk solved a real problem — a plant-based alternative to dairy that didn't taste like chalk. But it came with a water footprint nobody wanted to talk about.",
      { type: "h2", text: "The almond math" },
      "Roughly 1 almond takes 3.2 litres of water to grow. A typical carton of almond milk has ~2% almonds. You're paying for water in a box.",
      { type: "h2", text: "The millet math" },
      "Pearl millet grows on rain-fed marginal land. A litre of Milet Milk uses ~12x less water than a litre of almond milk.",
      { type: "h2", text: "Allergens" },
      "Millet is nut-free. Tree nut allergies are the second-fastest growing allergen category in kids. Millet sidesteps the whole issue.",
      {
        type: "quote",
        text: "A plant-based milk shouldn't cost the planet a river.",
      },
      "Almond had its decade. The next one belongs to grains.",
    ],
  },
  {
    slug: "millet-vs-corn",
    title: "Millet vs Corn — Whole Grain vs Syrup",
    dek: "One of these built America's snack aisle. The other is quietly building what's next.",
    tag: "Showdown",
    accent: "#41c1f6",
    readTime: "4 min read",
    image:
      "https://images.pexels.com/photos/547263/pexels-photo-547263.jpeg?auto=compress&cs=tinysrgb&w=940",
    body: [
      "Most of the corn grown in North America doesn't end up as sweet corn on your plate. It becomes feed, ethanol, or high-fructose corn syrup.",
      { type: "h2", text: "The supply chain" },
      "Corn is ~92% GMO in the US. Pesticide-heavy, soil-depleting, subsidy-dependent. Millet, by contrast, is a rotation crop that rebuilds soil health.",
      { type: "h2", text: "Nutrition" },
      "Whole corn is fine. The problem is you rarely eat whole corn. Millet stays whole in your kitchen by default — flour, grain, or milk.",
      {
        type: "quote",
        text: "Corn became a commodity. Millet is still a crop.",
      },
      "Nothing against a good corn tortilla. But your pantry can handle both.",
    ],
  },
  {
    slug: "millet-vs-soy",
    title: "Millet vs Soy — The Allergen Shift",
    dek: "Soy fed a generation of plant-based eaters. Millet picks up where it hit limits.",
    tag: "Showdown",
    accent: "#E7100E",
    readTime: "4 min read",
    image:
      "https://images.pexels.com/photos/16977458/pexels-photo-16977458.jpeg?auto=compress&cs=tinysrgb&w=940",
    body: [
      "Soy did the heavy lifting for plant-based food for decades. But it's on the top-8 allergen list for a reason, and the deforestation math gets harder every year.",
      { type: "h2", text: "Allergens" },
      "Soy allergies affect ~0.4% of adults and more kids. Millet sits outside the top-8 allergen list entirely.",
      { type: "h2", text: "Planetary cost" },
      "Soy is one of the top three drivers of Amazon deforestation. Pearl millet thrives on land where nothing else will grow.",
      {
        type: "quote",
        text: "Soy was the answer for a while. Millet is the answer for now.",
      },
      "Keep your tofu. Swap the soy latte.",
    ],
  },
  {
    slug: "millet-vs-whey-protein",
    title: "Millet vs Whey Protein — Real Food Beats Lab Powder",
    dek: "Whey is a powder. Millet is a meal.",
    tag: "Showdown",
    accent: "#1d168f",
    darkHeader: true,
    readTime: "4 min read",
    image:
      "https://images.pexels.com/photos/4162487/pexels-photo-4162487.jpeg?auto=compress&cs=tinysrgb&w=940",
    body: [
      "Whey exploded because it's convenient, fast-absorbing, and easy to dose. But convenience isn't the same as optimal.",
      { type: "h2", text: "What whey is" },
      "A dairy by-product, isolated and processed into powder. Lactose-sensitive people report bloating, acne, and gut issues.",
      { type: "h2", text: "What millet is" },
      "A whole food. 11-13g protein per 100g, plus iron, magnesium, fiber, and a microbiome-friendly starch profile.",
      {
        type: "quote",
        text: "Your body was never designed to run on a scoop.",
      },
      "Train hard. Eat millet. Keep the whey for race day if you must — don't let it be your baseline.",
    ],
  },
];

export const getBlog = (slug) => BLOGS.find((b) => b.slug === slug);
